import React, { useState, useEffect, useCallback } from 'react';
import {
  View, Text, Image, StyleSheet, TouchableOpacity, ScrollView,
  ActivityIndicator, useWindowDimensions,
} from 'react-native';
import {
  ref, listAll, getDownloadURL, getMetadata, getBlob, uploadBytes, deleteObject,
  StorageReference,
} from 'firebase/storage';
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db, storage } from '../firebase/firebase';
import { RefreshCw, UploadCloud, Check } from 'lucide-react';

const C = {
  primary: '#21808D', primaryLight: '#e8f5f7',
  danger: '#ef4444', dangerLight: '#fee2e2',
  success: '#16a34a', successLight: '#dcfce7',
  warn: '#d97706', warnLight: '#fef3c7',
  bg: '#f6f8fb', card: '#fff',
  textPrimary: '#1a2332', textMuted: '#64748b', border: '#e2e8f0',
};

const RESTAURANT_COLLECTION = 'zapee_restaurant_partner';
const PRODUCT_COLLECTION = 'zapee_restaurant_products';
const PHOTO_TYPES = ['logo', 'cover', 'banner', 'category', 'product', 'features'];
const COVER_FIELDS = ['media.cover', 'cover', 'media.coverImage', 'coverImage', 'coverPhoto', 'media.coverPhoto'];

interface Restaurant { id: string; store: string; name: string; root: string; data: any; }

const dirOf = (p: string) => { const i = p.lastIndexOf('/'); return i === -1 ? '(root)' : p.slice(0, i); };
async function listAllFiles(folderRef: StorageReference): Promise<StorageReference[]> {
  const res = await listAll(folderRef);
  let files = [...res.items];
  for (const prefix of res.prefixes) files = files.concat(await listAllFiles(prefix));
  return files;
}
function pathFromUrl(url: string): string | null {
  if (typeof url !== 'string' || !url.includes('/o/')) return null;
  const m = url.match(/\/o\/([^?]+)/);
  if (!m) return null;
  try { return decodeURIComponent(m[1]); } catch { return m[1]; }
}
const isPlainObject = (v: any) => v != null && typeof v === 'object' && Object.getPrototypeOf(v) === Object.prototype;
const getByPath = (obj: any, path: string) => path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);
function sanitizeFolder(raw: string): string {
  return raw.replace(/[#\[\]*?]/g, '').replace(/\\/g, '/').split('/').map(s => s.trim()).filter(Boolean).join('/');
}
function collectStorageUrls(value: any, out: Set<string>) {
  if (typeof value === 'string') { if (pathFromUrl(value)) out.add(value); }
  else if (Array.isArray(value)) value.forEach(v => collectStorageUrls(v, out));
  else if (isPlainObject(value)) Object.values(value).forEach(v => collectStorageUrls(v, out));
}
function deepReplace(value: any, repl: Map<string, string>): any {
  if (typeof value === 'string') return repl.get(value) ?? value;
  if (Array.isArray(value)) return value.map(v => deepReplace(v, repl));
  if (isPlainObject(value)) { const o: any = {}; for (const [k, v] of Object.entries(value)) o[k] = deepReplace(v, repl); return o; }
  return value;
}

// Resize + re-encode an image blob in the browser aiming for a target file size
// (default ~80KB). Lowers quality first, then shrinks dimensions, until it fits.
// Photos -> JPEG; images that may carry transparency (png/webp) -> WebP so logos
// aren't flattened to white. Returns null to keep the original (non-raster,
// error, or already smaller than the target).
const TARGET_BYTES = 80 * 1024; // 80 KB

async function compressImage(
  blob: Blob, contentType: string | undefined,
  targetBytes = TARGET_BYTES, startMaxDim = 1280,
): Promise<{ blob: Blob; contentType: string; ext: string } | null> {
  const type = (contentType || blob.type || '').toLowerCase();
  if (!/image\/(jpeg|jpg|png|webp)/.test(type)) return null; // skip gif/svg/etc.
  if (blob.size <= targetBytes) return null;                  // already small enough
  try {
    const bitmap = await createImageBitmap(blob);
    const outType = /png|webp/.test(type) ? 'image/webp' : 'image/jpeg';
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    let result: Blob | null = null;   // first encode that meets the target
    let smallest: Blob | null = null; // best effort if target can't be met

    outer:
    for (let maxDim = startMaxDim; maxDim >= 240; maxDim = Math.round(maxDim * 0.8)) {
      const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
      canvas.width = Math.max(1, Math.round(bitmap.width * scale));
      canvas.height = Math.max(1, Math.round(bitmap.height * scale));
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      for (let q = 0.82; q >= 0.4; q -= 0.12) {
        const out = await new Promise<Blob | null>(res => canvas.toBlob(res, outType, q));
        if (!out) continue;
        if (!smallest || out.size < smallest.size) smallest = out;
        if (out.size <= targetBytes) { result = out; break outer; }
      }
    }
    bitmap.close?.();

    const best = result || smallest;
    if (!best || best.size >= blob.size) return null; // no gain → keep original
    return { blob: best, contentType: outType, ext: outType === 'image/webp' ? '.webp' : '.jpg' };
  } catch { return null; }
}

export default function RestaurantDbSync() {
  const { width } = useWindowDimensions();
  const cols = width < 700 ? 3 : width < 1100 ? 4 : 6;

  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [productsByStore, setProductsByStore] = useState<Record<string, { id: string; data: any }[]>>({});
  const [existingPaths, setExistingPaths] = useState<Set<string>>(new Set());
  const [selRest, setSelRest] = useState<string>('');
  const [compress, setCompress] = useState(true);
  const [targetKB, setTargetKB] = useState(100); // compression target size
  const TARGET_OPTIONS = [100, 200, 300, 500];

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setProgress('Reading database…');
      const [restSnap, prodSnap] = await Promise.all([
        getDocs(collection(db, RESTAURANT_COLLECTION)),
        getDocs(collection(db, PRODUCT_COLLECTION)),
      ]);
      const rests: Restaurant[] = restSnap.docs.map(d => {
        const data: any = d.data();
        return {
          id: d.id,
          store: String(data.storeuid || data.uid || data.id || d.id),
          name: data.storename || data.name || d.id,
          root: sanitizeFolder(data.storename || data.name || ''),
          data,
        };
      }).filter(r => r.root).sort((a, b) => a.name.localeCompare(b.name));

      const prodBy: Record<string, { id: string; data: any }[]> = {};
      prodSnap.docs.forEach(p => {
        const data: any = p.data();
        (prodBy[String(data.branch || '')] ??= []).push({ id: p.id, data });
      });

      // Storage listing → set of actually-existing object paths, so "In folder"
      // reflects real presence, not just what the DB string says.
      setProgress('Checking storage…');
      let paths = new Set<string>();
      try {
        const refs = await listAllFiles(ref(storage));
        paths = new Set(refs.map(r => r.fullPath));
      } catch { /* listing blocked — fall back to path-string check */ }

      setRestaurants(rests);
      setProductsByStore(prodBy);
      setExistingPaths(paths);
      setSelRest(prev => prev && rests.some(r => r.id === prev) ? prev : (rests[0]?.id ?? ''));
    } catch (e: any) {
      setError(e?.message || 'Failed to load');
    } finally {
      setLoading(false);
      setProgress('');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rest = restaurants.find(r => r.id === selRest);

  // The DB-referenced image URLs for a restaurant + sub-folder type.
  const sourcesFor = (r: Restaurant, type: string): string[] => {
    if (type === 'logo') { const u = r.data?.media?.logo || r.data?.image; return u ? [u] : []; }
    if (type === 'cover') {
      for (const f of COVER_FIELDS) { const v = getByPath(r.data, f); if (typeof v === 'string' && pathFromUrl(v)) return [v]; }
      return [];
    }
    if (type === 'banner') return ((r.data?.media?.bannerImages || r.data?.bannerImages || []) as string[]).filter(Boolean);
    if (type === 'category') { const s = new Set<string>(); collectStorageUrls(r.data?.categories, s); return [...s]; }
    if (type === 'product') return (productsByStore[r.store] ?? []).map(p => p.data?.image).filter(Boolean);
    if (type === 'features') return (productsByStore[r.store] ?? []).flatMap(p => (p.data?.featureImages || [])).filter(Boolean);
    return [];
  };
  const inFolder = (url: string, r: Restaurant, type: string) => {
    const op = pathFromUrl(url);
    if (!op || dirOf(op) !== `${r.root}/${type}`) return false;
    // If we have a storage listing, require the file to actually exist there.
    return existingPaths.size === 0 ? true : existingPaths.has(op);
  };

  // Upload existing images for one type into <root>/<type> and update the DB.
  // Returns counts; does not reload / alert (so syncAll can batch).
  const processType = async (r: Restaurant, type: string, doCompress: boolean) => {
    const destFolder = `${r.root}/${type}`;
    const usedNames = new Set<string>();
    let moved = 0, failed = 0, docs = 0;

    const copyAndUrl = async (oldUrl: string): Promise<string | null> => {
      const op = pathFromUrl(oldUrl);
      if (!op || dirOf(op) === destFolder) return null;
      try {
        const srcRef = ref(storage, op);
        let ct: string | undefined;
        try { ct = (await getMetadata(srcRef)).contentType || undefined; } catch { /* ignore */ }
        const blob = await getBlob(srcRef);

        // Optionally compress before upload.
        let upBlob: Blob = blob, upType = ct;
        let base = op.split('/').pop() || `f_${moved}`;
        if (doCompress) {
          const comp = await compressImage(blob, ct, targetKB * 1024);
          if (comp) { upBlob = comp.blob; upType = comp.contentType; base = base.replace(/\.[^.]+$/, '') + comp.ext; }
        }

        let dst = `${destFolder}/${base}`;
        if (usedNames.has(dst)) {
          const dot = base.lastIndexOf('.');
          base = (dot > 0 ? base.slice(0, dot) : base) + `_${moved}` + (dot > 0 ? base.slice(dot) : '');
          dst = `${destFolder}/${base}`;
        }
        await uploadBytes(ref(storage, dst), upBlob, upType ? { contentType: upType } : undefined);
        const nu = await getDownloadURL(ref(storage, dst));
        usedNames.add(dst); moved++;
        setProgress(`Uploaded ${moved}…`);
        return nu;
      } catch { failed++; return null; }
    };
    const del = (oldUrl: string) => {
      const op = pathFromUrl(oldUrl);
      return op ? deleteObject(ref(storage, op)).catch(() => {}) : Promise.resolve();
    };

    if (type === 'logo') {
      const u = r.data?.media?.logo || r.data?.image || '';
      if (u) { const nu = await copyAndUrl(u); if (nu) { await updateDoc(doc(db, RESTAURANT_COLLECTION, r.id), { image: nu, 'media.logo': nu }); await del(u); docs++; } }
    } else if (type === 'cover') {
      let cf = '', cu = '';
      for (const f of COVER_FIELDS) { const v = getByPath(r.data, f); if (typeof v === 'string' && pathFromUrl(v)) { cu = v; cf = f; break; } }
      if (cu) { const nu = await copyAndUrl(cu); if (nu) { await updateDoc(doc(db, RESTAURANT_COLLECTION, r.id), { [cf]: nu }); await del(cu); docs++; } }
    } else if (type === 'banner') {
      const arr = (r.data?.media?.bannerImages || r.data?.bannerImages || []) as string[];
      const repl = new Map<string, string>();
      for (const u of arr) { const nu = await copyAndUrl(u); if (nu) repl.set(u, nu); }
      if (repl.size) { const na = arr.map(u => repl.get(u) ?? u); await updateDoc(doc(db, RESTAURANT_COLLECTION, r.id), { bannerImages: na, 'media.bannerImages': na }); await Promise.all([...repl.keys()].map(del)); docs++; }
    } else if (type === 'category') {
      const cats = r.data?.categories;
      const urls = new Set<string>(); collectStorageUrls(cats, urls);
      const repl = new Map<string, string>();
      for (const u of urls) { const nu = await copyAndUrl(u); if (nu) repl.set(u, nu); }
      if (repl.size) { await updateDoc(doc(db, RESTAURANT_COLLECTION, r.id), { categories: deepReplace(cats, repl) }); await Promise.all([...repl.keys()].map(del)); docs++; }
    } else if (type === 'product') {
      for (const p of (productsByStore[r.store] ?? [])) {
        const u = p.data?.image || ''; if (!u) continue;
        const nu = await copyAndUrl(u); if (nu) { await updateDoc(doc(db, PRODUCT_COLLECTION, p.id), { image: nu }); await del(u); docs++; }
      }
    } else if (type === 'features') {
      for (const p of (productsByStore[r.store] ?? [])) {
        const arr = (p.data?.featureImages || []) as string[]; if (!arr.length) continue;
        const repl = new Map<string, string>();
        for (const u of arr) { const nu = await copyAndUrl(u); if (nu) repl.set(u, nu); }
        if (repl.size) { const na = arr.map(u => repl.get(u) ?? u); await updateDoc(doc(db, PRODUCT_COLLECTION, p.id), { featureImages: na }); await Promise.all([...repl.keys()].map(del)); docs++; }
      }
    }
    return { moved, failed, docs };
  };

  const syncType = async (type: string) => {
    if (!rest) return;
    const need = sourcesFor(rest, type).filter(u => !inFolder(u, rest, type)).length;
    if (need === 0) { window.alert('All these images are already in the folder.'); return; }
    if (!window.confirm(`Upload ${need} "${type}" image(s) into ${rest.root}/${type} and update the database?`)) return;
    setWorking(true); setProgress('Uploading…');
    try {
      const c = await processType(rest, type, compress);
      setWorking(false); setProgress(''); await load();
      window.alert(`"${type}" done.\nUploaded: ${c.moved}\nRecords updated: ${c.docs}` +
        (c.failed ? `\nFailed: ${c.failed}` + (!c.moved ? ` (bucket may need CORS for downloads)` : '') : ''));
    } catch (e: any) { setWorking(false); setProgress(''); window.alert(`Failed: ${e?.message || e}`); }
  };

  const syncAll = async () => {
    if (!rest) return;
    const need = PHOTO_TYPES.reduce((n, t) => n + sourcesFor(rest, t).filter(u => !inFolder(u, rest, t)).length, 0);
    if (need === 0) { window.alert('Everything is already in its folder for this restaurant.'); return; }
    if (!window.confirm(`Upload all ${need} image(s) for "${rest.name}" into their sub-folders and update the database?`)) return;
    setWorking(true); setProgress('Uploading…');
    let moved = 0, failed = 0, docs = 0;
    try {
      for (const t of PHOTO_TYPES) { const c = await processType(rest, t, compress); moved += c.moved; failed += c.failed; docs += c.docs; }
      setWorking(false); setProgress(''); await load();
      window.alert(`"${rest.name}" done.\nUploaded: ${moved}\nRecords updated: ${docs}` +
        (failed ? `\nFailed: ${failed}` + (!moved ? ` (bucket may need CORS for downloads)` : '') : ''));
    } catch (e: any) { setWorking(false); setProgress(''); window.alert(`Failed: ${e?.message || e}`); }
  };

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color={C.primary} />
        <Text style={styles.progressText}>{progress || 'Loading…'}</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View style={{ flex: 1 }}>
          <Text style={styles.title}>Database → Directory Sync</Text>
          <Text style={styles.subtitle}>{restaurants.length} restaurants · upload existing DB images into their folders</Text>
        </View>
        <TouchableOpacity
          style={[styles.toggle, compress && styles.toggleOn]}
          onPress={() => setCompress(v => !v)}
          disabled={working}
        >
          {compress && <Check size={14} color="#fff" />}
          <Text style={[styles.toggleTxt, compress && { color: '#fff' }]}>Compress</Text>
        </TouchableOpacity>
        {compress && TARGET_OPTIONS.map(kb => (
          <TouchableOpacity
            key={kb}
            style={[styles.sizeChip, targetKB === kb && styles.sizeChipOn]}
            onPress={() => setTargetKB(kb)}
            disabled={working}
          >
            <Text style={[styles.sizeChipTxt, targetKB === kb && { color: '#fff' }]}>{kb}KB</Text>
          </TouchableOpacity>
        ))}
        <TouchableOpacity style={styles.refreshBtn} onPress={load} disabled={working}>
          <RefreshCw size={16} color="#fff" />
          <Text style={styles.refreshTxt}>Refresh</Text>
        </TouchableOpacity>
      </View>

      {error && <View style={styles.errorBox}><Text style={styles.errorTxt}>{error}</Text></View>}
      {working && (
        <View style={styles.workingBox}>
          <ActivityIndicator size="small" color={C.primary} />
          <Text style={styles.workingTxt}>{progress || 'Working…'}</Text>
        </View>
      )}

      {/* Restaurant top bar */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.topBar} contentContainerStyle={{ paddingRight: 16 }}>
        {restaurants.map(r => (
          <TouchableOpacity key={r.id} style={[styles.restTab, selRest === r.id && styles.restTabActive]} onPress={() => setSelRest(r.id)}>
            <Text style={[styles.restTabTxt, selRest === r.id && styles.restTabTxtActive]} numberOfLines={1}>{r.name}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      {/* Sub-folder sections for the selected restaurant */}
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 40 }}>
        {rest && (
          <View style={styles.restHeader}>
            <Text style={styles.restName}>{rest.root}</Text>
            <TouchableOpacity style={[styles.syncAllBtn, working && styles.btnDisabled]} onPress={syncAll} disabled={working}>
              <UploadCloud size={15} color="#fff" />
              <Text style={styles.syncAllTxt}>Upload all & update DB</Text>
            </TouchableOpacity>
          </View>
        )}

        {rest && PHOTO_TYPES.map(type => {
          const urls = sourcesFor(rest, type);
          const need = urls.filter(u => !inFolder(u, rest, type)).length;
          const done = urls.length - need;
          return (
            <View key={type} style={styles.section}>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionTitle}>{type}</Text>
                <Text style={styles.sectionMeta}>
                  {urls.length} image(s){urls.length ? ` · ${done} in folder · ${need} to upload` : ''}
                </Text>
                <View style={{ flex: 1 }} />
                <TouchableOpacity
                  style={[styles.syncBtn, (working || need === 0) && styles.btnDisabled]}
                  onPress={() => syncType(type)}
                  disabled={working || need === 0}
                >
                  <UploadCloud size={14} color="#fff" />
                  <Text style={styles.syncBtnTxt}>{need ? `Upload ${need} & update DB` : 'All in folder'}</Text>
                </TouchableOpacity>
              </View>

              {urls.length === 0 ? (
                <Text style={styles.muted}>No {type} images in the database.</Text>
              ) : (
                <View style={styles.grid}>
                  {urls.map((u, i) => {
                    const ok = inFolder(u, rest, type);
                    return (
                      <View key={`${u}_${i}`} style={[styles.cardWrap, { width: `${100 / cols}%` }]}>
                        <View style={styles.card}>
                          <View style={styles.thumbWrap}>
                            <Image source={{ uri: u }} style={styles.thumb} resizeMode="cover" />
                            <View style={[styles.badge, ok ? styles.badgeOk : styles.badgeNeed]}>
                              {ok ? <Check size={10} color="#fff" /> : null}
                              <Text style={styles.badgeTxt}>{ok ? 'In folder' : 'To upload'}</Text>
                            </View>
                          </View>
                        </View>
                      </View>
                    );
                  })}
                </View>
              )}
            </View>
          );
        })}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: C.bg, padding: 16 },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 40, minHeight: 200 },
  progressText: { marginTop: 14, color: C.textMuted, fontSize: 14 },
  muted: { color: C.textMuted, fontSize: 13 },

  header: { flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 },
  title: { fontSize: 22, fontWeight: '800', color: C.textPrimary },
  subtitle: { fontSize: 13, color: C.textMuted, marginTop: 2 },
  refreshBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: C.primary, paddingHorizontal: 14, paddingVertical: 9, borderRadius: 8 },
  refreshTxt: { color: '#fff', fontWeight: '600', fontSize: 13 },
  toggle: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    paddingHorizontal: 12, paddingVertical: 9, borderRadius: 8,
    backgroundColor: C.card, borderWidth: 1, borderColor: C.border,
  },
  toggleOn: { backgroundColor: C.success, borderColor: C.success },
  toggleTxt: { color: C.textMuted, fontWeight: '700', fontSize: 13 },
  sizeChip: {
    paddingHorizontal: 11, paddingVertical: 8, borderRadius: 8,
    backgroundColor: C.card, borderWidth: 1, borderColor: C.border,
  },
  sizeChipOn: { backgroundColor: C.primary, borderColor: C.primary },
  sizeChipTxt: { color: C.textMuted, fontWeight: '700', fontSize: 12 },

  errorBox: { backgroundColor: C.dangerLight, borderRadius: 8, padding: 12, marginBottom: 12 },
  errorTxt: { color: C.danger, fontSize: 13 },
  workingBox: { flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: C.primaryLight, borderRadius: 8, padding: 10, marginBottom: 12 },
  workingTxt: { color: C.primary, fontSize: 13, fontWeight: '600' },

  topBar: { flexGrow: 0, marginBottom: 12 },
  restTab: { paddingHorizontal: 16, paddingVertical: 10, borderRadius: 24, marginRight: 8, maxWidth: 220, backgroundColor: C.card, borderWidth: 1, borderColor: C.border },
  restTabActive: { backgroundColor: C.primary, borderColor: C.primary },
  restTabTxt: { fontSize: 13, color: C.textPrimary, fontWeight: '600' },
  restTabTxtActive: { color: '#fff' },

  restHeader: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 12 },
  restName: { fontSize: 18, fontWeight: '800', color: C.textPrimary, flex: 1 },
  syncAllBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: C.success, paddingHorizontal: 16, paddingVertical: 10, borderRadius: 8 },
  syncAllTxt: { color: '#fff', fontWeight: '800', fontSize: 13 },
  btnDisabled: { backgroundColor: '#94a3b8' },

  section: { backgroundColor: C.card, borderRadius: 12, borderWidth: 1, borderColor: C.border, padding: 14, marginBottom: 14 },
  sectionHeader: { flexDirection: 'row', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 },
  sectionTitle: { fontSize: 16, fontWeight: '700', color: C.textPrimary, textTransform: 'capitalize' },
  sectionMeta: { fontSize: 12, color: C.textMuted },
  syncBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: C.primary, paddingHorizontal: 14, paddingVertical: 8, borderRadius: 8 },
  syncBtnTxt: { color: '#fff', fontWeight: '700', fontSize: 12 },

  grid: { flexDirection: 'row', flexWrap: 'wrap', marginHorizontal: -5 },
  cardWrap: { paddingHorizontal: 5, marginBottom: 10 },
  card: { backgroundColor: C.card, borderRadius: 10, overflow: 'hidden', borderWidth: 1, borderColor: C.border },
  thumbWrap: { position: 'relative', width: '100%', aspectRatio: 1, backgroundColor: '#f1f5f9' },
  thumb: { width: '100%', height: '100%' },
  badge: { position: 'absolute', bottom: 6, left: 6, flexDirection: 'row', alignItems: 'center', gap: 3, paddingHorizontal: 6, paddingVertical: 2, borderRadius: 5 },
  badgeOk: { backgroundColor: C.success },
  badgeNeed: { backgroundColor: C.warn },
  badgeTxt: { color: '#fff', fontSize: 10, fontWeight: '700' },
});
